import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, computed, watch } from 'vue-demi';
import type { FormData } from './types';
import FormBuilder from './FormBuilder.vue';
import FormSuccess from './FormSuccess.vue';
import FormError from './FormError.vue';
import FormLoading from './FormLoading.vue';
import { useFormsConfig } from './composables/useFormsConfig';
import { DEFAULT_TEXTS } from './constants';


export default /*#__PURE__*/_defineComponent({
  __name: 'FormContainer',
  props: {
    formId: null,
    successMessage: null,
    hideFormTitle: { type: [Boolean, null] },
    hideFormDescription: { type: [Boolean, null] }
  },
  emits: ["submit", "submit-success", "submit-error", "form-start", "reset"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'submit', values: Record<string, any>): void;
  (e: 'submit-success'): void;
  (e: 'submit-error', error: string): void;
  (e: 'form-start'): void;
  (e: 'reset'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  formId: string;
  successMessage?: {
    title: string;
    description: string;
    buttonText: string;
  };
  hideFormTitle?: boolean | null;
  hideFormDescription?: boolean | null;
};



const { recaptcha, formsApi, locale } = useFormsConfig();

const successMessage = computed(
  () => props.successMessage || DEFAULT_TEXTS.success,
);

const recaptchaConfig = computed(() => {
  if (recaptcha && typeof recaptcha.siteKey === 'string') {
    return {
      siteKey: recaptcha.siteKey,
      theme: recaptcha.theme,
      size: recaptcha.size,
      locale: locale.value,
    };
  }
});



const formData = ref<FormData | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);
const isSubmitting = ref(false);
const showSuccessAlert = ref(false);

const loadForm = async () => {
  formData.value = null;
  if (!props.formId) return;

  isLoading.value = true;
  error.value = null;

  try {
    formData.value = await formsApi.getFormSchema(props.formId, {
      lang: locale.value,
    });
  } catch (e) {
    console.error('Error in loading form', e);
    error.value =
      e instanceof Error ? e.message : DEFAULT_TEXTS.errors.loadForm;
  } finally {
    isLoading.value = false;
  }
};

const onUpdateDependencyField = async (
  stepIndex: number,
  dependentFieldName: string,
  dependencyFieldValue: any,
) => {
  try {
    if (!formData.value) return;
    formData.value = await formsApi.getFormDataWithUpdatedDependentFieldData(
      stepIndex,
      dependentFieldName,
      dependencyFieldValue,
      formData.value,
    );
  } catch (e) {
    console.error(e);
  }
};

const handleSubmit = async (values: Record<string, any>) => {
  if (!formData.value) return;

  isSubmitting.value = true;
  error.value = null;
  emit('submit', values);

  try {
    await formsApi.submitForm(props.formId, values);
    showSuccessAlert.value = true;
    emit('submit-success');
  } catch (e) {
    const errorMessage =
      e instanceof Error ? e.message : DEFAULT_TEXTS.errors.submitForm;
    error.value = errorMessage;
    emit('submit-error', errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

const resetForm = () => {
  showSuccessAlert.value = false;
  error.value = null;
  emit('reset');
};

onMounted(async () => {
  await loadForm();
});

watch([() => props.formId, () => locale.value], () => {
  loadForm();
});

return { __sfc: true,props, recaptcha, formsApi, locale, successMessage, recaptchaConfig, emit, formData, isLoading, error, isSubmitting, showSuccessAlert, loadForm, onUpdateDependencyField, handleSubmit, resetForm, FormBuilder, FormSuccess, FormError, FormLoading }
}

})